import * as fx from '../audio/fx';
import * as engine from '../app/engine';
import { app, commit, on } from '../app/state';
import type { Theme } from '../core/types';
import { $, reducedMotion } from './dom';
import { closeSheet, isSheetOpen, openSheet } from './sheet';
import { closeFlyout } from './side-timers';
import { toast } from './toast';

/* ---------- theme: circular reveal from the toggle button ---------- */
let themeBusy = false;

function applyThemeAttr(t: Theme) {
  document.documentElement.dataset.theme = t;
}

/** Popovers register here so Escape closes the topmost one first (return true if it closed). */
const escapeHandlers: (() => boolean)[] = [];
export const onEscape = (fn: () => boolean) => escapeHandlers.push(fn);

export function setTheme(t: Theme, origin?: { x: number; y: number }) {
  const root = document.documentElement;
  const apply = () => {
    applyThemeAttr(t);
    commit(['settings'], (d) => (d.settings.theme = t));
  };
  const canVT = origin && !themeBusy && 'startViewTransition' in document && !reducedMotion();
  if (!canVT) return apply();
  themeBusy = true;
  root.classList.add('vt');
  const vt = document.startViewTransition(apply);
  const r = Math.hypot(Math.max(origin.x, innerWidth - origin.x), Math.max(origin.y, innerHeight - origin.y));
  vt.ready
    .then(() => {
      root.animate(
        {
          clipPath: [
            `circle(0px at ${origin.x}px ${origin.y}px)`,
            `circle(${r}px at ${origin.x}px ${origin.y}px)`,
          ],
        },
        { duration: 620, easing: 'cubic-bezier(.4,0,.2,1)', pseudoElement: '::view-transition-new(root)' },
      );
    })
    .catch(() => {});
  const done = () => {
    root.classList.remove('vt');
    themeBusy = false;
  };
  vt.finished.then(done, done);
}

const flipTheme = (origin?: { x: number; y: number }) =>
  setTheme(app.data.settings.theme === 'dark' ? 'light' : 'dark', origin);

/* ---------- zen ---------- */
function setZen(on: boolean) {
  document.body.classList.toggle('zen', on);
  $('#zenBtn').setAttribute('aria-pressed', String(on));
  try {
    if (on && !document.fullscreenElement)
      void document.documentElement.requestFullscreen?.().catch(() => {});
    else if (!on && document.fullscreenElement) void document.exitFullscreen?.().catch(() => {});
  } catch {
    /* fullscreen not allowed */
  }
  if (on) $('#btnStart').focus();
}
const toggleZen = () => setZen(!document.body.classList.contains('zen'));

/* ---------- keyboard ---------- */
/** Inputs that are not for typing: Escape must still close the dialog from these. */
const NON_TEXT_INPUTS = new Set(['checkbox', 'radio', 'range', 'button', 'submit', 'reset', 'color']);

function onKey(e: KeyboardEvent) {
  if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
  const el = e.target as HTMLElement;
  const tag = el.tagName?.toLowerCase();
  const field = tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
  const typing = field && !(el instanceof HTMLInputElement && NON_TEXT_INPUTS.has(el.type));
  if (typing || (field && e.key !== 'Escape')) return;
  if (e.key === 'Escape') {
    if (closeFlyout()) return;
    if (escapeHandlers.some((fn) => fn())) return;
    if (isSheetOpen()) return closeSheet();
    if (document.body.classList.contains('zen')) return setZen(false);
    return;
  }
  // Inside the dialog only Escape is global; everything else belongs to the dialog's controls.
  if (isSheetOpen()) return;
  // A focused button handles its own Space/Enter (keyboard activation keeps focus — B7).
  if ((tag === 'button' || tag === 'a') && (e.code === 'Space' || e.key === 'Enter')) return;
  const k = e.key.toLowerCase();
  if (e.code === 'Space') {
    e.preventDefault();
    engine.toggle();
  } else if (k === 'r') engine.reset();
  else if (k === 's') engine.skip();
  else if (k === 'i') {
    if (!engine.logInterruption()) toast('Interruptions are logged during a focus session');
  } else if (k === 'f') toggleZen();
  else if (k === 't') flipTheme();
  else if (k === 'n') openSheet('tasks');
}

export function initChrome() {
  applyThemeAttr(app.data.settings.theme);
  on('settings', () => applyThemeAttr(app.data.settings.theme));
  $('#zenBtn').addEventListener('click', toggleZen);
  $('#zenClose').addEventListener('click', () => setZen(false));
  // Leaving fullscreen with the browser's own Esc must also leave zen (B8).
  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement && document.body.classList.contains('zen')) setZen(false);
  });
  window.addEventListener('keydown', onKey);
  window.addEventListener('pointerdown', fx.ensureAudio, { passive: true });
}
